import React from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, Activity, Clock, TestTube, Ambulance, Building2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FixedWidgets from '../components/FixedWidgets';
import ot from '../assets/OT.jpeg';
import icu from '../assets/ICU.jpeg';
import lab from '../assets/lab.jpeg';
import xray from '../assets/x-ray.png';
import s from '../assets/special.jpeg';
import sh from '../assets/sharing.jpeg';
import cori from '../assets/cori.jpeg';
import ward from '../assets/ward.jpeg';
import ipd from '../assets/ipd.jpeg';
import waiting from '../assets/waiting.jpeg';
import ambu from '../assets/ambu.jpeg';
import pharm from '../assets/pharm.png';

const FacilitiesPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services247 = [
    { 
      icon: ClipboardList, // Changed from Heart to better represent General Medicine
      name: 'General Medicine', 
      description: 'Comprehensive primary care, chronic disease management, and diagnostic expertise for adult health.' 
    },
    { icon: Activity, name: 'ICU Services', description: 'NICU, and ICCU with latest monitoring equipment' },
    { icon: Clock, name: 'Emergency & Trauma', description: 'Round-the-clock emergency services with expert trauma care' },
    { icon: TestTube, name: 'Laboratory', description: 'State-of-the-art clinical laboratory with quick results' },
    { icon: Ambulance, name: 'Ambulance', description: 'Well-equipped ambulances with trained paramedics' },
    { icon: Building2, name: 'Pharmacy', description: '24/7 pharmacy with comprehensive medicine availability' }
  ];

  const facilities = [
    {
      title: 'Waiting Area',
      description: 'A spacious and organized Out-Patient waiting area equipped with a streamlined token management system, ensuring minimal wait times and a disciplined flow of consultations across all medical specialties.',
      image: waiting
    },
    {
      title: 'Hi-Tech Operation Theaters',
      description: 'Our surgical wing features specialized Major and Minor Operation Theaters equipped with world-class infrastructure. These suites facilitate a full range of interventions—from complex life-saving surgeries to minimally invasive procedures—all designed to prioritize patient safety, reduce recovery times, and ensure clinical excellence.',
      image: ot
    },
    {
      title: 'Intensive Care Units',
      description: 'Comprehensive 24/7 critical care infrastructure featuring specialized NICU and ICCU facilities, blending advanced ventilator technology with expert bedside nursing.',
      image: icu
    },
    {
      title: 'Hi-Tech Laboratory',
      description: 'A fully automated diagnostic hub delivering high-precision results through advanced pathology, biochemistry, and molecular testing technologies for reliable clinical decision-making.',
      image: lab
    },
    {
      title: 'X-Ray',
      description: 'Experience rapid diagnostics with our advanced digital radiography system, featuring instant image processing that allows our specialists to provide immediate clinical evaluations and faster treatment starts.',
      image: xray
    },
    {
      title: 'Spacious Corridors',
      description: 'Designed for seamless patient transport, our wide and well-ventilated corridors ensure easy mobility for stretchers and wheelchairs, maintaining a clutter-free environment for safety and efficiency.',
      image: cori
    },
    {
      title: 'Deluxe and AC Rooms',
      description: 'Experience a restful recovery in our premium Deluxe AC suites, designed with elegant interiors, ample space for attendants, and personalized amenities to ensure maximum comfort for both patients and their families.',
      image: s
    },
    {
      title: 'Sharing Rooms',
      description: 'Our semi-private sharing rooms offer a cost-effective healthcare solution, providing a clean and organized environment with dedicated bed-side space for attendants and essential patient monitoring.',
      image: sh
    },
    {
      title: 'Male & Female Wards',
      description: 'Separated Male and Female wings featuring well-ventilated layouts and strictly maintained hygiene standards. Each bed is equipped with essential medical support systems and professional nursing oversight around the clock.',
      image: ward
    },
    {
      title: 'In Patitent Department',
      description: 'Our In-Patient Department (IPD) provides a seamless recovery journey, integrating specialized ICUs with comfortable ward accommodations to ensure continuous medical monitoring and personalized nursing care for all hospitalized patients.',
      image: ipd
    },
    {
      title: 'Pharmacy',
      description: 'Providing 24/7 pharmaceutical support, our chemist outlet ensures immediate access to genuine medicines with a streamlined dispensing process to save you time and effort during your hospital visit.',
      image: pharm
    },
    {
      title: 'Ambulance',
      description: 'Our 24/7 emergency ambulance fleet is equipped with advanced life-support systems and cardiac monitors, serving as a critical mobile ICU to provide immediate stabilization during transit.',
      image: ambu
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <FixedWidgets />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-20">
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0 z-0"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 to-[#4A90A4]/80 z-10" />
          <img
            src="https://images.unsplash.com/photo-1597807037496-c56a1d8bc29a"
            alt="Facilities"
            className="w-full h-full object-cover"
          />
        </motion.div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold text-white mb-6"
          >
            World-Class <span className="text-[#6FCF97]">Facilities</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-gray-200 max-w-3xl mx-auto"
          >
            State-of-the-art medical infrastructure for comprehensive care
          </motion.p>
        </div>
      </section>

      {/* 24/7 Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              24/7 <span className="text-[#4A90A4]">Available Services</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Round-the-clock services for your health emergencies
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services247.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10, scale: 1.03 }}
                className="bg-white p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 text-center flex flex-col items-center"

              >
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                  className="w-16 h-16 bg-gradient-to-br from-[#4A90A4] to-[#6FCF97] rounded-2xl flex items-center justify-center mb-6"
                >
                  <service.icon className="text-white" size={28} />
                </motion.div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities Showcase */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Advanced <span className="text-[#4A90A4]">Infrastructure</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Equipped with cutting-edge medical technology
            </p>
          </motion.div>

          <div className="space-y-16">
            {facilities.map((facility, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="lg:w-1/2"
                >
                  <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src={facility.image}
                      alt={facility.title}
                      className="w-full h-96 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/50 to-transparent" />
                  </div>
                </motion.div>

                <div className="lg:w-1/2">
                  <motion.h3
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="text-3xl font-bold text-gray-900 mb-4"
                  >
                    {facility.title}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="text-gray-600 text-lg leading-relaxed"
                  >
                    {facility.description}
                  </motion.p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FacilitiesPage;